import React from "react";
import { Alert, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CartesianGrid, Line, LineChart, Pie, PieChart, Tooltip, XAxis, YAxis } from "recharts";

import { useAppApi } from "@/App";
import { ExportButton } from "@components/Common/Button";
import { DevForm } from "@components/Forms/Fields";
import { RenderForm } from "@components/Forms/Render";
import { DefaultLayout } from "@components/Layouts";
import { CommonCard } from "@components/Pages";
import { money_fmt, url_reverse } from "@components/Utils";

export default function GamesReportScreen() {
    return (
        <DefaultLayout>
            <GamesReport />
        </DefaultLayout>
    );
}

function GamesReport() {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:statements:games_report"));

    if (!apiData) return;

    const developerReportUrl = <Link to={url_reverse("statements:developers_report")}>Разработчики</Link>;

    const chartData = [];
    let maxSum = 0;
    if (apiData.type === "normal") {
        for (const val of Object.values(apiData.report?.slice(0, 4) || {})) {
            chartData.push({
                name: val.name,
                value: Number(val.percent),
            });
        }
    }
    if (apiData.type === "group") {
        for (const [key, val] of Object.entries(apiData.periods || {})) {
            chartData.push({
                name: key,
                rub: val,
                amt: val,
            });
            maxSum = Math.max(maxSum, val);
        }
    }
    const renderLabel = function (entry) {
        return entry.name;
    };

    const buttons = (
        <>
            <DevForm.Button variant="primary" type="submit" name="build">
                {t("Построить")}
            </DevForm.Button>{" "}
            <ExportButton
                prms={{
                    "main-stats_period": apiData.forms_data?.main.data.stats_period,
                    "main-stats_group": apiData.forms_data?.main.data.stats_group,
                    "main-stats_type": apiData.forms_data?.main.data.stats_type,
                    "main-excluded_developers": apiData.forms_data?.main.data.excluded_developers,
                    "main-group_dlc": apiData.forms_data?.main.data.group_dlc,
                }}
            />
        </>
    );

    return (
        <CommonCard header={<div>Игры / {developerReportUrl}</div>}>
            <RenderForm buttons={buttons} />

            {apiData.report?.length === 0 && (
                <Alert variant={"danger"} className="mt-3">
                    {t("Нет данных за этот период")}
                </Alert>
            )}

            {apiData.report?.length > 0 && (
                <>
                    {apiData.type === "normal" && (
                        <div className="d-flex align-items-center justify-content-center">
                            <PieChart width={1200} height={400}>
                                <Pie
                                    data={chartData}
                                    dataKey="value"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={150}
                                    fill="#8884d8"
                                    label={renderLabel}
                                />
                            </PieChart>
                        </div>
                    )}
                    {apiData.type === "group" && (
                        <div className="d-flex align-items-center justify-content-center">
                            <LineChart
                                width={1000}
                                height={500}
                                data={chartData}
                                margin={{ top: 20, right: 30, left: 35, bottom: 50 }}
                            >
                                <Line type="monotone" dataKey="rub" stroke="#8884d8" />
                                <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                                <XAxis dataKey="name" />
                                <YAxis domain={[0, maxSum]} />
                                <Tooltip />
                            </LineChart>
                        </div>
                    )}
                    <Table responsive className="dmg-table mt-2">
                        <thead>
                            <tr>
                                {apiData.type === "normal" && (
                                    <>
                                        <th scope="col">{t("Игра")}</th>
                                        <th scope="col">{t("Заработок игры, руб.")}</th>
                                        <th scope="col">{t("% к общему количеству продаж")}</th>
                                        <th scope="col">{t("Основной жанр")}</th>
                                    </>
                                )}
                                {apiData.type === "group" && (
                                    <>
                                        <th scope="col">{t("Игра")}</th>
                                        {Object.entries(apiData.periods)?.map(([key]) => (
                                            <th scope="col" key={key}>
                                                {key}
                                            </th>
                                        ))}
                                    </>
                                )}
                                {apiData.type === "genre" && (
                                    <>
                                        <th scope="col">{t("Основной жанр")}</th>
                                        <th scope="col">{t("Количество игр в жанре")}</th>
                                        <th scope="col">{t("Сумма платежей")}</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {apiData.type === "normal" && apiData.total_sum && (
                                <>
                                    <tr key="total">
                                        <td scope="col">
                                            <b>Общее количество продаж</b>
                                        </td>
                                        <td scope="col">
                                            <b>{money_fmt(apiData.total_sum)}</b>
                                        </td>
                                        <td scope="col"></td>
                                        <td scope="col"></td>
                                    </tr>
                                    {apiData.report?.map((obj) => (
                                        <tr key={obj.game_id + obj.total_sum + obj.main_genre}>
                                            <td scope="col">{obj.name}</td>
                                            <td scope="col">{money_fmt(obj.total_sum)}</td>
                                            <td scope="col">{obj.percent}</td>
                                            <td scope="col">{obj.main_genre}</td>
                                        </tr>
                                    ))}
                                </>
                            )}
                            {apiData.type === "group" && (
                                <>
                                    <tr key="total">
                                        <td scope="col">
                                            <b>Общее количество продаж</b>
                                        </td>
                                        {Object.entries(apiData.periods)?.map(([key, value]) => (
                                            <td scope="col" key={key}>
                                                <b>{value ? money_fmt(value) : null}</b>
                                            </td>
                                        ))}
                                    </tr>
                                    {apiData.report?.map((obj) => (
                                        <tr key={obj.game_id}>
                                            <td scope="col">{obj.name}</td>
                                            {Object.keys(apiData.periods)?.map((key) => (
                                                <td scope="col" key={key}>
                                                    {obj.periods[key] ? money_fmt(obj.periods[key]) : null}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </>
                            )}
                            {apiData.type === "genre" && (
                                <>
                                    <tr key="total">
                                        <td scope="col">
                                            <b>Общее количество продаж</b>
                                        </td>
                                        <td scope="col"></td>
                                        <td scope="col">
                                            <b>{money_fmt(apiData.total_sum)}</b>
                                        </td>
                                    </tr>
                                    {apiData.report?.map((obj) => (
                                        <tr key={obj.main_genre}>
                                            <td scope="col">{obj.main_genre}</td>
                                            <td scope="col">{obj.cnt}</td>
                                            <td scope="col">{money_fmt(obj.total_sum)}</td>
                                        </tr>
                                    ))}
                                </>
                            )}
                        </tbody>
                    </Table>
                </>
            )}
        </CommonCard>
    );
}
